// src/pages/VideoCatalog/index.jsx
import React, { useState, useEffect } from "react";
import { Form, Input, Select, Upload, Button, Card, notification } from "antd";
import {
  SearchOutlined,
  UploadOutlined,
  PlayCircleOutlined,
} from "@ant-design/icons";
import {
  useDeleteVideoMutation,
  useGetInterpretersQuery,
  useGetOrganizationsQuery,
  useGetVideoCatalogQuery,
  useUpdateVideoMutation,
  useUploadFileMutation,
  useUploadVideoMutation,
} from "../../../store/api/apiSlice";
import { languageOptions } from "../../../utils";
import UploadVideoModal from "../../../components/Modals/UploadVideoModal";
import VideoInfoModal from "../../../components/Modals/VideoInfoModal";
import DeleteVideoModal from "../../../components/Modals/DeleteVideoModal";
import VideoOverview from "./VideoOverview";
import VideoCard from "./VideoCard";

const { Option } = Select;
const VideoCatalog = () => {
  const [form] = Form.useForm();
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [selectedPresenter, setSelectedPresenter] = useState(null);
  const [sort, setSortBy] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [playingVideoId, setPlayingVideoId] = useState(null);
  const [videoDurations, setVideoDurations] = useState({});
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  // Queries

  const getQueryParams = () => {
    const params = {};

    if (searchTerm?.trim()) {
      params.search = searchTerm.trim();
    }

    if (selectedOrg) {
      params.organization = selectedOrg;
    }

    if (selectedPresenter) {
      params.interpreter = selectedPresenter;
    }

    if (sort) {
      params.sort = sort;
    }

    if (selectedLanguage) {
      params.language = selectedLanguage;
    }

    return params;
  };

  const { data: videosData, isLoading: videosLoading } =
    useGetVideoCatalogQuery(getQueryParams());

  const { data: orgsData } = useGetOrganizationsQuery();
  const { data: interpretersData } = useGetInterpretersQuery();
  const [uploadVideo] = useUploadVideoMutation();
  const [uploadFile] = useUploadFileMutation();
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [videoInfoVisible, setVideoInfoVisible] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [updateVideo] = useUpdateVideoMutation();
  const [deleteVideo] = useDeleteVideoMutation();

  const handleEdit = () => {
    // Set form values including file and selections
    form.setFieldsValue({
      title: selectedVideo?.title,
      description: selectedVideo?.description,
      interpreter: selectedVideo?.interpreter?._id,
      organization: selectedVideo?.organization?._id,
      language: selectedVideo?.language,
    });

    // Set the file list with existing video URL
    setFileList([
      {
        uid: "-1",
        name: "current-video.mp4",
        status: "done",
        url: selectedVideo?.url,
        // Add preview URL for video
        thumbUrl: selectedVideo?.url, // This will show video preview
        type: "video/mp4",
      },
    ]);

    setIsEditMode(true);
    setVideoInfoVisible(false);
    setUploadModalVisible(true);
  };

  const handleVideoClick = (video) => {
    setSelectedVideo(video);
    setVideoInfoVisible(true);
  };
  const handleDeleteVideo = async () => {
    try {
      await deleteVideo(selectedVideo?._id).unwrap();
      notification.success({
        message: "Success",
        description: "Video deleted successfully",
      });
      setVideoInfoVisible(false);
      setDeleteModalVisible(false);
    } catch (error) {
      notification.error({
        message: "Error",
        description: error.message || "Failed to delete video",
      });
    }
  };

  const handleUpload = async (values) => {
    try {
      setUploading(true);

      let videoUrl = selectedVideo?.url; // Keep existing URL if no new file

      // Only upload new file if one is selected (and it's not the existing video)
      if (fileList.length > 0 && fileList[0] && fileList[0].originFileObj) {
        const fileUploadResponse = await uploadFile({
          file: fileList[0].originFileObj,
          filename: fileList[0].originFileObj.name,
        }).unwrap();
        videoUrl = fileUploadResponse.file;
      }

      const videoData = {
        title: values.title,
        description: values.description,
        organization: values.organization,
        interpreter: values.interpreter,
        language: values.language,
        url: videoUrl,
        type:
          fileList[0]?.originFileObj?.type ||
          fileList[0]?.type ||
          selectedVideo?.type,
      };

      if (selectedVideo) {
        // Update existing video
        await updateVideo({
          id: selectedVideo?._id,
          data: videoData,
        }).unwrap();
      } else {
        // Create new video
        await uploadVideo(videoData).unwrap();
      }

      notification.success({
        message: "Success",
        description: selectedVideo
          ? "Video updated successfully"
          : "Video uploaded successfully",
      });
      setUploadModalVisible(false);
      resetModalState();
    } catch (error) {
      notification.error({
        message: "Error",
        description: error.message || "Failed to process video",
      });
    } finally {
      setUploading(false);
    }
  };

  const uploadProps = {
    accept: "video/*",
    beforeUpload: (file) => {
      const isVideo = file.type.startsWith("video/");
      if (!isVideo) {
        notification.error({
          message: "Invalid File",
          description: "Please upload video files only (MP4, WebM, etc.)",
        });
        return Upload.LIST_IGNORE;
      }
      const isLt2GB = file.size / 1024 / 1024 / 1024 < 2;
      if (!isLt2GB) {
        notification.error({
          message: "File Too Large",
          description: "Video must be smaller than 2GB",
        });
        return Upload.LIST_IGNORE;
      }
      setFileList([file]);
      return false;
    },
    fileList,
    onRemove: () => {
      setFileList([]);
    },
    listType: "picture", // Add this to show preview
    showUploadList: {
      showPreviewIcon: true,
      showDownloadIcon: false,
      showRemoveIcon: true,
    },
  };
  const resetModalState = () => {
    form.resetFields();
    setFileList([]);
    setSelectedVideo(null);
    setIsEditMode(false);
  };
  const handleModalClose = () => {
    resetModalState();
    setUploadModalVisible(false);
  };
  const handleOpenUploadModal = () => {
    resetModalState(); // Reset form when opening for new upload
    setUploadModalVisible(true);
  };

  const handleVideoLoad = (videoId, event) => {
    const duration = Math.floor(event.target.duration);
    const minutes = Math.floor(duration / 60);
    const seconds = duration % 60;
    setVideoDurations((prev) => ({
      ...prev,
      [videoId]: `${minutes.toString().padStart(2, "0")}:${seconds
        .toString()
        .padStart(2, "0")}`,
    }));
  };

  const handleVideoPlayPause = (videoId, videoRef) => {
    if (playingVideoId === videoId) {
      if (videoRef.current.paused) {
        videoRef.current.play();
      } else {
        videoRef.current.pause();
      }
    } else {
      // Pause previous video if any
      if (playingVideoId) {
        document.querySelector(`#video-${playingVideoId}`)?.pause();
      }
      videoRef.current.play();
      setPlayingVideoId(videoId);
    }
  };

  return (
    <div className="">
      <div className="flex flex-col md:flex-row justify-between gap-4 m-0 mt-2 mb-4 pb-2 items-end border-b border-gray-200">
        <div className="flex-1">
          <h1
            className={`md:text-2l text-xl font-bold text-gray-900 mb-0`}
          >
            Video Catalogue
          </h1>
          <p className="text-lightBlack font-light text-sm w-[80%] mt-1 mb-0">
            Manage and associate videos to Organisation
          </p>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <Button
            type="primary"
            size="large"
            icon={<UploadOutlined />}
            onClick={handleOpenUploadModal}
            className="flex items-center gap-2 bg-[#18181B]"
          >
            Upload a Video
          </Button>
        </div>
      </div>
      <VideoOverview videosData={videosData} />
      <div className="flex flex-wrap gap-4 mb-8">
        <div className="flex-1 min-w-[200px]">
          <Input
            prefix={<SearchOutlined className="text-gray-400" />}
            placeholder="Search..."
            onChange={(e) => setSearchTerm(e.target.value)}
            className="rounded-xl h-11"
            allowClear
          />
        </div>
        <Select
          allowClear
          placeholder="Language"
          className="min-w-[150px] rounded-xl h-11"
          onChange={(value) => setSelectedLanguage(value)} // Add this state
        >
          {languageOptions.map((option) => (
            <Option key={option.value} value={option.value}>
              {option.label}
            </Option>
          ))}
        </Select>

        <Select
          allowClear
          placeholder="Organisation"
          className="min-w-[200px] rounded-xl h-11"
          onChange={(value) => setSelectedOrg(value)}
        >
          {orgsData?.data?.map((org) => (
            <Option key={org._id} value={org._id}>
              {org.name}
            </Option>
          ))}
        </Select>
        <Select
          allowClear
          placeholder="Presenter"
          className="min-w-[200px] rounded-xl h-11"
          onChange={(value) => setSelectedPresenter(value)}
        >
          {interpretersData?.data?.map((interpreter) => (
            <Option key={interpreter._id} value={interpreter._id}>
              {interpreter?.user?.full_name || "Unnamed Interpreter"}
            </Option>
          ))}
        </Select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {videosLoading ? (
          // Loading skeleton
          [...Array(4)].map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="h-48 bg-gray-200 rounded-2xl mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
          ))
        ) : videosData?.data?.videoCatalog?.length > 0 ? (
          videosData?.data?.videoCatalog.map((video) => (
            <VideoCard
              key={video._id}
              video={video}
              videoDurations={videoDurations}
              playingVideoId={playingVideoId}
              onVideoClick={handleVideoClick}
              onVideoLoad={handleVideoLoad}
              onVideoPlayPause={handleVideoPlayPause}
            />
          ))
        ) : (
          // Empty state
          <div className="col-span-full flex flex-col items-center justify-center py-12 px-4">
            <div className="text-gray-400 mb-3">
              <i className="text-5xl">
                <PlayCircleOutlined />
              </i>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-1">
              No videos found
            </h3>
            <p className="text-gray-500 text-center">
              Upload a video or adjust your filters to see results
            </p>
          </div>
        )}
      </div>

      {/* Upload Modal */}
      <UploadVideoModal
        visible={uploadModalVisible}
        onCancel={() => setUploadModalVisible(false)}
        onFinish={handleUpload}
        form={form}
        uploading={uploading}
        fileList={fileList}
        setFileList={setFileList}
        selectedVideo={selectedVideo}
        interpretersData={interpretersData}
        orgsData={orgsData}
        title={isEditMode ? "Update Video" : "Upload Video"}
        isEditMode={isEditMode}
      />
      <VideoInfoModal
        visible={videoInfoVisible}
        onCancel={() => setVideoInfoVisible(false)}
        selectedVideo={selectedVideo}
        onEdit={handleEdit}
        onDelete={() => setDeleteModalVisible(true)}
      />
      <DeleteVideoModal
        visible={deleteModalVisible}
        onCancel={() => setDeleteModalVisible(false)}
        onConfirm={handleDeleteVideo}
      />
    </div>
  );
};

export default VideoCatalog;
